import { useEffect, useState } from 'react';
import { nowFormatted, fromNowEs, humanizeDuration } from '@/utils/dateUtils';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  List,
  ListItem,
  ListItemText,
  Box,
  Divider,
  Stack,
  IconButton,
  CircularProgress,
  Tooltip,
  useTheme,
  Card,
  CardContent,
  LinearProgress,
  Switch,
  FormControlLabel,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import DnsIcon from '@mui/icons-material/Dns';
import BoltIcon from '@mui/icons-material/Bolt';
import DevicesIcon from '@mui/icons-material/Devices';
import StorageIcon from '@mui/icons-material/Storage';
import ComputerIcon from '@mui/icons-material/Computer';
import RefreshIcon from '@mui/icons-material/Refresh';
import { getDebugUIEnabled, setDebugUIEnabled } from '@/hooks/useDebugLog';

type Props = {
  open: boolean;
  onClose: () => void;
  status: 'ok' | 'warning' | 'error' | 'unknown';
  details?: string;
  errors?: string[];
  wsState?: string | null;
  wsReason?: string | null;
  onRefresh?: () => void;
};

type HealthData = {
  status?: string;
  timestamp?: number;
  version?: string | null;
  environment?: string | null;
  uptime_seconds?: number;
  summary?: Record<string, any> | null;
  api?: {
    status?: string;
    uptime_seconds?: number;
    version?: string | null;
    environment?: string | null;
    memory?: Record<string, any>;
    cpu?: Record<string, any>;
    requests?: { in_flight?: number };
  } | null;
  websocket?: {
    state?: string;
    connections_active?: number;
    admin_connections?: number;
    agents_connected?: number;
    recent_reconnects?: number;
  } | null;
  agents?: {
    total?: number;
    connected?: number;
    disconnected?: number;
    with_error?: number;
    last_connected?: { agent_id?: string | null; timestamp?: string | null } | null;
  } | null;
  database?: {
    status?: string;
    engine?: string;
    ping_ms?: number;
    pool?: Record<string, any> | null;
    last_error?: string | null;
  } | null;
  system?: {
    hostname?: string;
    pid?: number;
    os?: string;
    arch?: string;
    load_average?: number[] | null;
    disk?: Record<string, any> | null;
  } | null;
};

type PropsExt = Props & {
  wsLastMessageAt?: number | null;
  health?: HealthData | null;
};

function getLabel(status: Props['status']) {
  switch (status) {
    case 'ok':
      return 'Operativo';
    case 'warning':
      return 'Advertencia';
    case 'error':
      return 'Error';
    default:
      return 'Desconocido';
  }
}
export default function SystemStatusModal({
  open,
  onClose,
  status,
  details,
  errors,
  wsState,
  wsReason,
  onRefresh,
  wsLastMessageAt,
  health,
}: PropsExt) {
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [debugEnabled, setDebugEnabled] = useState<boolean>(() => getDebugUIEnabled());

  useEffect(() => {
    if (open) {
      setLastUpdated(nowFormatted());
    }
  }, [open, health, wsLastMessageAt]);

  const handleRefresh = async () => {
    if (!onRefresh) return;
    try {
      setLoading(true);
      await onRefresh();
      setLastUpdated(nowFormatted());
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async (text?: string) => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (_e) {
      // silent fail
    }
  };

  const copyAllErrors = async () => {
    if (!errors || errors.length === 0) return;
    await copyToClipboard(errors.join('\n'));
  };

  const mapStatusColor = (s: 'ok' | 'warning' | 'error' | 'unknown') => {
    if (s === 'ok') return theme.palette.success.main; // todo bien -> verde
    if (s === 'warning') return theme.palette.warning.main; // parcial / sin datos -> amarillo
    if (s === 'error') return theme.palette.error.main; // todo mal -> rojo
    return theme.palette.warning.main; // unknown -> mostrar amarillo (sin datos)
  };

  const renderStatusChip = (label: string, s: 'ok' | 'warning' | 'error' | 'unknown') => {
    const color = mapStatusColor(s);
    // Render a small circular indicator (dot) with the state's color.
    // Keep a tooltip so users can read the textual status on hover.
    return (
      <Tooltip title={label ?? ''}>
        <Box
          sx={{
            width: 12,
            height: 12,
            borderRadius: '50%',
            bgcolor: color,
            border: `2px solid ${theme.palette.background.paper}`,
          }}
        />
      </Tooltip>
    );
  };

  const wsLastAgo = wsLastMessageAt ? Math.round((Date.now() - wsLastMessageAt) / 1000) : null;

  const formatGB = (bytes?: number) => {
    if (typeof bytes !== 'number' || Number.isNaN(bytes)) return '—';
    return `${(bytes / 1024 ** 3).toFixed(1)} GB`;
  };

  const formatUptime = (s?: number) => {
    if (typeof s !== 'number' || Number.isNaN(s)) return '—';
    return humanizeDuration(s, true);
  };

  const renderLoadGraph = (la?: number[] | null) => {
    const labels = ['1m', '5m', '15m'];
    const loads = Array.isArray(la) ? la.slice(0, 3) : [0, 0, 0];
    while (loads.length < 3) loads.push(0);

    const getCpuCores = () => {
      const sysCpu = (health as any)?.system?.cpu;
      const apiCpu = (health as any)?.api?.cpu;
      const coresFromHealth = sysCpu?.cores ?? apiCpu?.cores;
      if (typeof coresFromHealth === 'number' && coresFromHealth > 0) return coresFromHealth;
      if (typeof navigator !== 'undefined' && (navigator as any).hardwareConcurrency)
        return (navigator as any).hardwareConcurrency as number;
      return 1;
    };

    const cores = getCpuCores();
    const percents = loads.map((v) => Math.min(100, Math.round((v / Math.max(1, cores)) * 100)));

    // Stacked Area Chart SVG (3 series stacked)
    // increase virtual width so the chart has more horizontal spacing
    const w = 260;
    // give extra vertical room for larger labels (increased +5px)
    const h = 69;
    // increase internal padding so labels don't get clipped
    const pad = 14;
    const innerW = w - pad * 2;
    const innerH = h - pad * 2;

    // cumulative percents for stacking, capped at 100
    const cum: number[] = [];
    let s = 0;
    for (let i = 0; i < percents.length; i++) {
      s += percents[i] ?? 0;
      cum.push(Math.min(100, s));
    }

    const maxIdx = percents.reduce(
      (best, cur, idx) => (cur > (percents[best] ?? 0) ? idx : best),
      0
    );

    const xFor = (i: number) => pad + innerW * (i / Math.max(1, percents.length - 1));
    const yFor = (valuePercent: number) => pad + innerH * (1 - valuePercent / 100);

    const layerColors = percents.map((p) =>
      mapStatusColor(p <= 50 ? 'ok' : p <= 80 ? 'warning' : 'error')
    );

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.5 }}>
        <Tooltip title={percents.map((p, i) => `${labels[i]} ${p}%`).join(' · ')}>
          <Box sx={{ width: '100%', height: `${h}px` }}>
            <Box
              component="svg"
              width="100%"
              height="100%"
              viewBox={`0 0 ${w} ${h}`}
              preserveAspectRatio="xMinYMid meet"
              sx={{ display: 'block', width: '100%', height: '100%' }}
            >
              {/* render layers from bottom to top */}
              {cum.map((_, layerIdx) => {
                const topPoints = cum.map((c) => yFor(c));
                // previous cumulative (bottom of this layer)
                // build path from left->right topPoints then close to baseline
                const topPath = topPoints.map((y, i) => `${xFor(i)} ${y}`).join(' L ');
                const d = `M ${topPath} L ${pad + innerW} ${pad + innerH} L ${pad} ${pad + innerH} Z`;

                const color = layerColors[layerIdx];
                return (
                  <path
                    key={layerIdx}
                    d={d}
                    fill={color}
                    fillOpacity={0.18 + layerIdx * 0.06}
                    stroke="none"
                  />
                );
              })}

              {/* top markers line */}
              <polyline
                points={percents.map((_, i) => `${xFor(i)},${yFor(cum[i] ?? 0)}`).join(' ')}
                fill="none"
                stroke={theme.palette.primary.main}
                strokeWidth={1.5}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              {/* horizontal max line */}
              <line
                x1={pad}
                x2={pad + innerW}
                y1={yFor(100)}
                y2={yFor(100)}
                stroke={theme.palette.divider}
                strokeWidth={1}
                strokeDasharray="4 2"
              />
              <text
                x={pad + innerW}
                y={yFor(100) - 2}
                fontSize={11}
                textAnchor="end"
                fontFamily={theme.typography.fontFamily}
                fill={theme.palette.text.secondary}
              >
                Máx 100%
              </text>
              {/* vertical value lines for each point */}
              {percents.map((_, i) => {
                const x = xFor(i);
                const y = yFor(cum[i] ?? 0);
                return (
                  <line
                    key={`v-${i}`}
                    x1={x}
                    x2={x}
                    y1={yFor(100)}
                    y2={y}
                    stroke={theme.palette.divider}
                    strokeDasharray="2 2"
                    strokeWidth={0.8}
                  />
                );
              })}
              {/* percentage labels and max marker */}
              {percents.map((p, i) => {
                const x = xFor(i);
                const y = yFor(cum[i] ?? 0);
                return (
                  <g key={`lbl-${i}`}>
                    <text
                      x={x}
                      y={y - 10}
                      fontSize={13}
                      textAnchor="middle"
                      fontFamily={theme.typography.fontFamily}
                      fill={theme.palette.primary.main}
                    >{`${p}%`}</text>
                    {i === maxIdx && (
                      <line
                        x1={x}
                        x2={x}
                        y1={pad}
                        y2={h - pad}
                        stroke={theme.palette.primary.light}
                        strokeDasharray="3 2"
                        strokeWidth={1.4}
                      />
                    )}
                  </g>
                );
              })}

              {/* centered Máx label */}
              <text
                x={pad + innerW / 2}
                y={pad + 8}
                fontSize={13}
                textAnchor="middle"
                fontFamily={theme.typography.fontFamily}
                fill={theme.palette.primary.main}
              >
                Máx {percents[maxIdx]}%
              </text>
            </Box>
          </Box>
        </Tooltip>
      </Box>
    );
  };

  // derive database status from health if available
  const dbHealthy = health?.database
    ? health.database.status === 'healthy' || health.database.status === 'ok'
    : undefined;
  // derive normalized states for visual consistency
  const backendState: 'ok' | 'warning' | 'error' | 'unknown' = health?.status
    ? String(health.status).toLowerCase() === 'healthy'
      ? 'ok'
      : String(health.status).toLowerCase() === 'degraded'
        ? 'warning'
        : 'error'
    : 'unknown';

  const wsNormalizedState: 'ok' | 'warning' | 'error' | 'unknown' = (() => {
    const raw = (wsState ?? '').toUpperCase();
    if (raw === 'OPEN') return 'ok';
    if (raw === 'CONNECTING') return 'warning';
    if (raw === 'CLOSING') return 'warning';
    if (raw === 'CLOSED' || raw === 'ERROR') return 'error';
    if (raw === 'NO_WS' || raw === '') return 'unknown';
    return 'warning';
  })();

  const dbState: 'ok' | 'warning' | 'error' | 'unknown' =
    typeof dbHealthy === 'boolean' ? (dbHealthy ? 'ok' : 'error') : 'unknown';

  const overallState: 'ok' | 'warning' | 'error' | 'unknown' = health?.status
    ? backendState
    : status;
  const overallLabel = String(health?.status ?? status ?? 'Sin datos');
  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      slotProps={{ paper: { sx: { maxWidth: 'calc(100vw - 52px)' } } }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          <Typography variant="h6">Estado del Sistema</Typography>
          <Typography variant="caption" color="text.secondary">
            {getLabel(status)} · Última actualización: {lastUpdated ?? '—'}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          {onRefresh && (
            <Tooltip title={loading ? 'Actualizando...' : 'Actualizar'}>
              <span>
                <IconButton onClick={handleRefresh} size="small" disabled={loading}>
                  {loading ? <CircularProgress size={18} /> : <RefreshIcon />}
                </IconButton>
              </span>
            </Tooltip>
          )}
          {renderStatusChip(overallLabel, overallState)}
        </Stack>
      </DialogTitle>
      <Divider sx={{ my: 2 }} />
      <DialogContent
        sx={{
          px: 2,
          pt: 1,
          pb: 2,
          maxHeight: '70vh',
          overflowY: 'auto',
          overflowX: 'hidden',
          boxSizing: 'border-box',
        }}
      >
        {/* Detailed sections */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
          <Box>
            <Card variant="outlined">
              <CardContent sx={{ overflowX: 'hidden', boxSizing: 'border-box' }}>
                <Stack spacing={1}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Stack direction="row" spacing={1} alignItems="center">
                      <DnsIcon color="action" />
                      <Typography variant="subtitle1">Backend API</Typography>
                    </Stack>
                    {renderStatusChip(
                      health?.status ? String(health.status) : 'Sin datos',
                      backendState
                    )}
                  </Stack>

                  <Typography variant="body2" color="text.secondary">
                    {details ?? 'Sin detalles disponibles.'}
                  </Typography>
                  <Stack direction="row" spacing={2}>
                    <Typography variant="caption" color="text.secondary">
                      Versión: {health?.version ?? health?.api?.version ?? '—'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Entorno: {health?.environment ?? health?.api?.environment ?? '—'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Desde: {formatUptime(health?.uptime_seconds ?? health?.api?.uptime_seconds)}
                    </Typography>
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
          </Box>

          <Box>
            <Card variant="outlined">
              <CardContent>
                <Stack spacing={1}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Stack direction="row" spacing={1} alignItems="center">
                      <BoltIcon color="action" />
                      <Typography variant="subtitle1">WebSocket</Typography>
                    </Stack>
                    {renderStatusChip(
                      health?.websocket?.state
                        ? String(health.websocket.state)
                        : wsState
                          ? wsState === 'OPEN'
                            ? 'Conectado'
                            : wsState
                          : 'Sin datos',
                      wsNormalizedState
                    )}
                  </Stack>

                  <Typography variant="body2" color="text.secondary">
                    {health?.websocket
                      ? `Estado: ${health.websocket.state ?? '—'}`
                      : (wsReason ?? 'Sin motivo disponible')}
                  </Typography>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="caption" color="text.secondary">
                      Conexiones: {health?.websocket?.connections_active ?? '—'}
                    </Typography>
                    {wsLastAgo !== null && (
                      <Typography
                        variant="caption"
                        color={wsLastAgo > 60 ? 'warning.main' : 'text.secondary'}
                        sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                      >
                        <AccessTimeIcon fontSize="small" /> Último mensaje:{' '}
                        {wsLastMessageAt
                          ? fromNowEs(wsLastMessageAt)
                          : wsLastAgo !== null
                            ? humanizeDuration(wsLastAgo, true)
                            : '—'}
                      </Typography>
                    )}
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
          </Box>

          <Box>
            <Card variant="outlined">
              <CardContent>
                <Stack spacing={1}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Stack direction="row" spacing={1} alignItems="center">
                      <StorageIcon color="action" />
                      <Typography variant="subtitle1">Base de datos</Typography>
                    </Stack>
                    {renderStatusChip(
                      health?.database
                        ? health.database.status === 'healthy'
                          ? 'OK'
                          : String(health.database.status ?? 'Error')
                        : 'Sin datos',
                      dbState
                    )}
                  </Stack>

                  <Stack spacing={0.5}>
                    <Typography variant="caption" color="text.secondary">
                      Motor: {health?.database?.engine ?? '—'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Ping: {health?.database?.ping_ms ?? '—'} ms
                    </Typography>
                    {/* Pool occupancy */}
                    {health?.database?.pool && (
                      <>
                        <Typography variant="caption" color="text.secondary">
                          Pool de conexiones
                        </Typography>
                        {typeof health.database.pool.inUse === 'number' &&
                        typeof health.database.pool.max === 'number' ? (
                          <LinearProgress
                            variant="determinate"
                            value={Math.min(
                              100,
                              (health.database.pool.inUse / Math.max(1, health.database.pool.max)) *
                                100
                            )}
                            sx={{ height: 10, borderRadius: 2 }}
                          />
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            {JSON.stringify(health.database.pool)}
                          </Typography>
                        )}
                      </>
                    )}

                    {health?.database?.last_error ? (
                      <Typography
                        variant="body2"
                        color="error.main"
                        sx={{ fontFamily: 'monospace' }}
                      >
                        {health.database.last_error}
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        Sin errores reportados.
                      </Typography>
                    )}
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
          </Box>

          <Box>
            <Card variant="outlined">
              <CardContent>
                <Stack spacing={1}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Stack direction="row" spacing={1} alignItems="center">
                      <DevicesIcon color="action" />
                      <Typography variant="subtitle1">Agentes</Typography>
                    </Stack>
                    <Typography variant="caption" color="text.secondary">
                      {health?.agents?.connected ?? 0} / {health?.agents?.total ?? 0}
                    </Typography>
                  </Stack>

                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Conectados
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      value={Math.min(
                        100,
                        ((health?.agents?.connected ?? 0) /
                          Math.max(1, health?.agents?.total ?? 1)) *
                          100
                      )}
                      sx={{ height: 10, borderRadius: 2, mt: 0.5 }}
                    />
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      justifyContent="space-between"
                      sx={{ mt: 0.5 }}
                    >
                      <Typography variant="caption" color="text.secondary">
                        Con errores: {health?.agents?.with_error ?? 0}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Último: {health?.agents?.last_connected?.agent_id ?? '—'}
                      </Typography>
                    </Stack>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Box>

          {/* Conditional errors section */}
          {errors && errors.length > 0 && (
            <Box sx={{ gridColumn: '1 / -1' }}>
              <Card variant="outlined">
                <CardContent>
                  <Stack spacing={1}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Stack direction="row" spacing={1} alignItems="center">
                        <ErrorOutlineIcon color="error" />
                        <Typography variant="subtitle1">Errores ({errors.length})</Typography>
                      </Stack>
                      <Tooltip title={copied ? 'Copiado' : 'Copiar todos'}>
                        <IconButton size="small" onClick={copyAllErrors}>
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>

                    <List dense>
                      {errors.map((e, i) => (
                        <ListItem
                          key={i}
                          secondaryAction={
                            <Tooltip title="Copiar">
                              <IconButton
                                edge="end"
                                size="small"
                                onClick={() => copyToClipboard(e)}
                              >
                                <ContentCopyIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          }
                        >
                          <ListItemText
                            primary={
                              <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                {e}
                              </Typography>
                            }
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Stack>
                </CardContent>
              </Card>
            </Box>
          )}

          {/* System / Host info */}
          <Box sx={{ gridColumn: '1 / -1' }}>
            <Card variant="outlined">
              <CardContent sx={{ display: 'flex', flexDirection: 'column', minHeight: 120 }}>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 280px' },
                    gap: 2,
                    width: '100%',
                  }}
                >
                  <Box>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <ComputerIcon />
                      <Typography variant="subtitle1">Sistema / Host</Typography>
                    </Stack>
                    <Stack spacing={0.5} sx={{ mt: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        Host: {health?.system?.hostname ?? '—'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        PID: {health?.system?.pid ?? '—'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        OS / Arch: {health?.system?.os ?? '—'} / {health?.system?.arch ?? '—'}
                      </Typography>
                    </Stack>
                  </Box>

                  <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                    <Typography variant="caption" color="text.secondary">
                      Carga (1m,5m,15m)
                    </Typography>
                    <Box sx={{ flex: 1, minHeight: 0 }}>
                      {renderLoadGraph(health?.system?.load_average)}
                    </Box>
                  </Box>

                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Disco
                    </Typography>
                    {health?.system?.disk &&
                    typeof health.system.disk.used === 'number' &&
                    typeof health.system.disk.total === 'number' ? (
                      <>
                        <LinearProgress
                          variant="determinate"
                          value={Math.min(
                            100,
                            (health.system.disk.used / Math.max(1, health.system.disk.total)) * 100
                          )}
                          sx={{ height: 10, borderRadius: 2, mt: 0.5 }}
                        />
                        <Typography variant="caption" color="text.secondary">
                          {Math.round((health.system.disk.used / health.system.disk.total) * 100)}%
                          · {formatGB(health.system.disk.used)} /{' '}
                          {formatGB(health.system.disk.total)}
                        </Typography>
                      </>
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        {health?.system?.disk ? JSON.stringify(health.system.disk) : '—'}
                      </Typography>
                    )}
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Box sx={{ flex: 1, px: 2 }}>
          <Typography variant="caption" color="text.secondary">
            Fuente: Backend · WebSocket
          </Typography>
        </Box>
        <FormControlLabel
          control={
            <Switch
              size="small"
              color="primary"
              checked={debugEnabled}
              onChange={(_e, checked) => {
                try {
                  setDebugUIEnabled(checked);
                  setDebugEnabled(checked);
                } catch (_err) {
                  // ignore
                }
              }}
            />
          }
          label="Habilitar Debug"
        />
        <Button onClick={onClose} color="primary">
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );
}
